<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { fade } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faXmark, faSpinner } from '@fortawesome/pro-light-svg-icons';

  export let show = false;
  export let studyGUID = '';
  export let siteGUID = '';
  export let protocolNumber = '';
  export let siteId = '';

  const dispatch = createEventDispatcher();

  let loading = false;
  let error = '';
  let success = '';
  let exportJobId = '';
  let exportJobStatus = null;
  let pollTimer = null;

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  onDestroy(() => {
    stopPolling();
  });

  function handleClose() {
    stopPolling();
    error = '';
    success = '';
    exportJobId = '';
    exportJobStatus = null;
    dispatch('close');
  }

  function startPolling(jobId) {
    stopPolling();
    pollTimer = setInterval(() => pollStatus(jobId), 3000);
  }

  async function pollStatus(jobId) {
    try {
      const response = await fetch(`/api/v1/site-cgm-exports/status/${jobId}`);
      if (!response.ok) {
        return;
      }

      const result = await response.json();
      if (!result.success) {
        return;
      }

      exportJobStatus = result;
      if (result.status === 'completed') {
        stopPolling();
        success = 'Export complete.';
        dispatch('completed');
      } else if (result.status === 'failed') {
        stopPolling();
        error = result.error || 'Export failed';
      }
    } catch (err) {
      console.error('Error polling site export status:', err);
    }
  }

  async function startExport() {
    if (!studyGUID || !siteGUID) {
      error = 'Study and site must be selected';
      return;
    }

    loading = true;
    error = '';
    success = '';

    try {
      const response = await fetch('/api/v1/site-cgm-exports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studyGUID, siteGUID })
      });

      const result = await response.json();
      if (!response.ok || !result.success || !result.jobId) {
        throw new Error(result.message || 'Failed to start export');
      }

      exportJobId = result.jobId;
      exportJobStatus = {
        status: 'processing',
        progress: {
          currentSubject: '',
          completedSubjects: 0,
          totalSubjects: 0
        }
      };
      startPolling(result.jobId);
    } catch (err) {
      console.error('Error starting site export:', err);
      error = err.message || 'Failed to start export';
    } finally {
      loading = false;
    }
  }
</script>

{#if show}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm" transition:fade>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="absolute inset-0 bg-slate-800 opacity-75" on:click={handleClose}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="relative bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-lg w-full mx-4 z-[60]" on:click|stopPropagation>
      <div class="flex justify-between items-center mb-4">
        <div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Site CGM Export</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">{protocolNumber} / {siteId}</p>
        </div>
        <button
          type="button"
          class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
          on:click={handleClose}
          title="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div class="space-y-4">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Start a site-level export that creates one CSV per participant and stores the ZIP on S3 for later download.
        </p>

        {#if error}
          <div class="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        {/if}

        {#if success}
          <div class="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300">
            {success}
          </div>
        {/if}

        {#if exportJobStatus}
          <div class="rounded border border-gray-200 dark:border-gray-700 px-4 py-3">
            <div class="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
              {#if exportJobStatus.status === 'processing'}
                <FontAwesomeIcon icon={faSpinner} class="animate-spin" />
              {/if}
              <span>Status: {exportJobStatus.status}</span>
            </div>
            <div class="mt-2 text-sm text-gray-600 dark:text-gray-300">
              <div>Completed participants: {exportJobStatus.progress?.completedSubjects || 0} / {exportJobStatus.progress?.totalSubjects || 0}</div>
              {#if exportJobStatus.progress?.currentSubject}
                <div>Current participant: {exportJobStatus.progress.currentSubject}</div>
              {/if}
            </div>
          </div>
        {/if}

        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 rounded border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200"
            on:click={handleClose}
          >
            Close
          </button>
          {#if exportJobStatus?.status !== 'completed'}
            <button
              type="button"
              class="px-4 py-2 rounded bg-sky-700 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              on:click={startExport}
              disabled={loading || (exportJobStatus?.status === 'processing')}
            >
              {#if loading}
                Starting...
              {:else if exportJobStatus?.status === 'processing'}
                Export running...
              {:else}
                Start export
              {/if}
            </button>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}
